<script setup lang="ts">
import { computed, ref } from 'vue';
import { TrashIcon } from '@heroicons/vue/24/outline';

import BxCard from '@/components/common/BxCard.vue';
import { ApplicantType } from '@/helpers/const';
import type { ApplicantEntry, ApplicantTypeEnum } from '@/types/Applicant';
import ApplicantForm from './ApplicantForm.vue';

const props = withDefaults(
  defineProps<{
    type: ApplicantTypeEnum;
    modelValue: ApplicantEntry;
    primary?: ApplicantEntry;
    allApplicants?: ApplicantEntry[];
    currentId?: string;
  }>(),
  {
    primary: undefined,
    allApplicants: () => [],
    currentId: '',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: ApplicantEntry): void;
  (e: 'edit', id: string): void;
  (e: 'remove', value: string): void;
  (e: 'relationship-change', applicantId: string, newStatus: string, oldStatus?: string): void;
}>();

const applicantRef = ref<InstanceType<typeof ApplicantForm>>();

const applicant = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const displayName = computed(() => {
  if (props.modelValue.firstName) return props.modelValue.firstName;
  const nameType = props.type === ApplicantType.PRIMARY ? 'Primary' : 'Secondary';
  return `${nameType} borrower`;
});

const update = async () => {
  return (await applicantRef.value?.update()) ?? false;
};

const cancel = () => {
  applicantRef.value?.cancel();
};

const validateForAdd = async () => {
  return (await applicantRef.value?.validateForAdd()) ?? false;
};

defineExpose({ update, cancel, validateForAdd });
</script>

<template>
  <div class="my-6 gap-4">
    <BxCard>
      <h3 class="mb-8 flex items-center justify-between text-lg font-medium leading-6 text-gray-900">
        <span class="flex items-center">
          <span class="mr-3 hidden md:block"><BxIcon name="customer" /></span>
          <span>{{ displayName }}</span>
        </span>
        <button
          v-if="type === ApplicantType.SECONDARY"
          type="button"
          :data-test-id="`applicant-${applicant.id}-remove`"
          class="ml-4 text-gray-400 transition hover:text-red-500"
          aria-label="Remove borrower"
          @click="emit('remove', applicant.id)"
        >
          <TrashIcon class="h-5 w-5" aria-hidden="true" />
        </button>
      </h3>
      <ApplicantForm
        ref="applicantRef"
        v-model="applicant"
        :type="type"
        :primary="primary"
        :all-applicants="allApplicants"
        :current-id="currentId"
        @relationship-change="
          (applicantId, newStatus, oldStatus) => emit('relationship-change', applicantId, newStatus, oldStatus)
        "
      />
    </BxCard>
  </div>
</template>
